import { Component, HostListener, Input } from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { forkJoin } from 'rxjs';
import { Destination, DestinationsService, FileType } from 'ldt-data-deliveries-api';
import { Ledger } from '../ledger.types';
import { ContactSearchFilter, SearchLedgerContactsRequest } from 'ldt-ledger-service-api-v2';
import { NotificationService } from 'src/app/shared/notification-service/notification.service';
import { AlertType } from 'src/app/shared/alert/alert.component';
import { DownloadStatusService } from 'src/app/shared/download-status/download-status.service';
import { LedgerDownloadService } from '../ledger-download.service';

export type LedgerDownloadScope = 'view' | 'all';

interface FormatOption {
  value: FileType;
  label: string;
  description: string;
}

@Component({
  selector: 'app-ledger-download-drawer',
  templateUrl: './ledger-download-drawer.component.html',
  animations: [
    trigger('drawerAnimation', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate('300ms ease-out', style({ transform: 'translateX(0)' })),
      ]),
      transition(':leave', [animate('300ms ease-in', style({ transform: 'translateX(100%)' }))]),
    ]),
    trigger('backdropAnimation', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('300ms ease-out', style({ opacity: 1 })),
      ]),
      transition(':leave', [animate('300ms ease-in', style({ opacity: 0 }))]),
    ]),
  ],
  standalone: false,
})
export class LedgerDownloadDrawerComponent {
  // Below this count, direct CSV downloads skip the async delivery flow and fetch the
  // file synchronously from the ledger service.
  private static readonly SYNC_DOWNLOAD_LIMIT = 10000;

  @Input() ledger: Ledger;
  @Input() orgId: string;

  isOpen = false;
  scope: LedgerDownloadScope = 'all';
  contactCount: number | null = null;
  submitting = false;
  alertType = AlertType;

  formats: FormatOption[] = [
    {
      value: FileType.Csv,
      label: 'CSV',
      description: 'Contact fields in a flat CSV, easily usable in tools like Excel.',
    },
    {
      value: FileType.JsonlGz,
      label: 'JSONL',
      description: 'Gzipped JSONL, ideal for machine analysis and programmatic use.',
    },
  ];
  selectedFormat: FileType = FileType.Csv;

  // null means manual download (fetch the file from the browser when ready)
  destinations: Destination[] = [];
  selectedDestinationId: number | null = null;
  loadingDestinations = false;
  private destinationTypeNames = new Map<number, string>();

  private filters: ContactSearchFilter[] = [];
  private operator?: SearchLedgerContactsRequest.OperatorEnum;

  constructor(
    private destinationsService: DestinationsService,
    private ledgerDownloadService: LedgerDownloadService,
    private downloadStatusService: DownloadStatusService,
    private notify: NotificationService
  ) {}

  @HostListener('document:keydown.escape')
  onEscape() {
    if (this.isOpen && !this.submitting) {
      this.dismiss();
    }
  }

  open(
    scope: LedgerDownloadScope,
    filters: ContactSearchFilter[],
    contactCount: number | null,
    operator?: SearchLedgerContactsRequest.OperatorEnum
  ) {
    this.scope = scope;
    this.filters = filters;
    this.operator = operator;
    this.contactCount = contactCount;
    this.selectedFormat = FileType.Csv;
    this.selectedDestinationId = null;
    this.submitting = false;
    this.isOpen = true;
    this.loadDestinations();
  }

  dismiss() {
    // Keep the drawer up while a request is in flight so the user can't start a duplicate
    // export before the first one is acknowledged
    if (this.submitting) {
      return;
    }
    this.isOpen = false;
  }

  get scopeLabel(): string {
    return this.scope === 'view' ? 'Current View' : 'All Contacts';
  }

  get selectedDestination(): Destination | undefined {
    return this.destinations.find((d) => d.id === this.selectedDestinationId);
  }

  destinationTypeName(destination: Destination): string | undefined {
    return this.destinationTypeNames.get(destination.destination_type_id);
  }

  get integrationsLink(): string {
    return '/' + this.orgId + '/integrations/available-integrations';
  }

  private loadDestinations() {
    this.loadingDestinations = true;
    forkJoin({
      destinations: this.destinationsService.listDestinations(this.orgId, { size: 100 }),
      types: this.destinationsService.listDestinationTypes(this.orgId),
    }).subscribe({
      next: ({ destinations, types }) => {
        this.destinationTypeNames = new Map(types.map((t) => [t.id, t.display_name]));
        // Email destinations can't receive file exports, so only offer file-based ones
        const emailTypeIds = new Set(
          types.filter((t) => t.display_name?.toLowerCase().includes('email')).map((t) => t.id)
        );
        this.destinations = (destinations.items || []).filter(
          (d) => !d.deleted_at && !emailTypeIds.has(d.destination_type_id)
        );
        this.loadingDestinations = false;
      },
      error: () => {
        // Destinations are optional for downloads; fall back to manual download only
        this.destinations = [];
        this.loadingDestinations = false;
      },
    });
  }

  startDownload() {
    this.submitting = true;

    const destination = this.selectedDestination;

    // Small direct CSV downloads don't need the delivery machinery
    if (
      !destination &&
      this.selectedFormat === FileType.Csv &&
      this.contactCount !== null &&
      this.contactCount < LedgerDownloadDrawerComponent.SYNC_DOWNLOAD_LIMIT
    ) {
      this.ledgerDownloadService
        .directDownloadLedgerContacts(this.orgId, this.ledger, this.filters, this.operator)
        .subscribe({
          next: () => {
            this.submitting = false;
            this.dismiss();
          },
          error: () => {
            this.notify.error(
              'Oops. There was an error during your request. Please try again later.'
            );
            this.submitting = false;
          },
        });
      return;
    }
    this.ledgerDownloadService
      .downloadLedgerContacts(
        this.orgId,
        this.ledger,
        this.filters,
        this.selectedFormat,
        destination?.id,
        this.operator
      )
      .subscribe({
        next: (deliveryId) => {
          if (destination) {
            this.notify.success('Export started. It will be sent to ' + destination.name + '.');
          } else {
            this.downloadStatusService.showDownloadStatus(deliveryId);
          }
          this.submitting = false;
          this.dismiss();
        },
        error: () => {
          this.notify.error(
            'Oops. There was an error during your request. Please try again later.'
          );
          this.submitting = false;
        },
      });
  }
}
